import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Divider,
  Avatar
} from '@mui/material';
import {
  Search as SearchIcon,
  Assessment as AssessmentIcon,
  Receipt as ReceiptIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Report category types
interface ReportItem {
  name: string;
  description: string;
  path: string;
  isImplemented: boolean;
  isFavorite?: boolean;
  reportType?: string;
}

interface ReportCategory {
  title: string;
  icon: React.ReactElement;
  color: string;
  reports: ReportItem[];
}

const ReportsDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  // Starred out of the box. Names must match the `name` of the report entries below.
  const [favorites, setFavorites] = useState<string[]>([
    'Day Book (Combined Ledger View)',
    'Profit And Loss Report',
    'Balance Sheet',
    'GSTR-1 (Sales)',
  ]);

  // Report categories based on the existing ReportsTable.tsx
  const reportCategories: ReportCategory[] = [
    {
      title: 'GST Reports',
      icon: <ReceiptIcon />,
      color: '#1976d2',
      reports: [
        {
          name: 'TDS Payable',
          description: 'Tax deducted at source payable report',
          path: '/reports/tds-payable',
          isImplemented: true,
          reportType: 'TDS_PAYABLE'
        },
        {
          name: 'TDS Receivable',
          description: 'Tax deducted at source receivable report',
          path: '/reports/tds-receivables',
          isImplemented: true,
          reportType: 'TDS_RECEIVEABLES'
        },
        {
          name: 'TCS Payable',
          description: 'Tax collected at source payable report',
          path: '/reports/tcs-payable',
          isImplemented: false,
          reportType: 'TCS_PAYABLE'
        },
        {
          name: 'TCS Receivable',
          description: 'Tax collected at source receivable report',
          path: '/reports/tcs-receivables',
          isImplemented: false,
          reportType: 'TCS_RECEIVEABLES'
        },
        {
          name: 'GSTR-1 (Sales)',
          description: 'GST return for outward supplies',
          path: '/reports/gstr-1-sales',
          isImplemented: true
        },
        {
          name: 'GSTR-2 (Purchase)',
          description: 'GST return for inward supplies',
          path: '/reports/gstr-2-purchase',
          isImplemented: true
        }
      ]
    },
    {
      title: 'Transaction Reports',
      icon: <TrendingUpIcon />,
      color: '#388e3c',
      reports: [
        {
          name: 'Day Book (Combined Ledger View)',
          description: 'Journal Entry Ledgers Statement - Complete unified view of all accounting transactions',
          path: '/reports/day-book',
          isImplemented: true,
          reportType: 'DAY_BOOK'
        },
        {
          name: 'Journal Entries - Party-Wise Ledger',
          description: 'Detailed party-wise ledger breakdown with dynamic filters',
          path: '/reports/journal-entries-party-ledger',
          isImplemented: true
        },
        {
          name: 'Profit And Loss Report',
          description: 'Comprehensive P&L statement following Indian AS',
          path: '/reports/profit-loss',
          isImplemented: true
        },
        {
          name: 'Balance Sheet',
          description: 'Statement of assets, liabilities and equity',
          path: '/reports/balance-sheet',
          isImplemented: true
        },
        // {
        //   name: 'Balance Sheet - Party Wise',
        //   description: 'Balance sheet with detailed party-wise breakdown',
        //   path: '/reports/balance-sheet-party-wise',
        //   isImplemented: true
        // },
        {
          name: 'Activity Logs',
          description: 'Complete transaction activity logs',
          path: '/audit-log',
          isImplemented: true
        },
        {
          name: 'Bill Wise Profit',
          description: 'Profit analysis by individual bills',
          path: '/reports/bill-wise-profit',
          isImplemented: false
        },
        {
          name: 'Cash and Bank Report (All Payments)',
          description: 'Comprehensive cash and bank transactions',
          path: '/reports/cash-and-bank-report-all-payments',
          isImplemented: true
        },
        {
          name: 'Daybook - Staff Wise',
          description: 'Daily transaction summary by staff',
          path: '/reports/daybook-staff-wise',
          isImplemented: false
        },
        {
          name: 'Expense Category Report',
          description: 'Analytical expense breakdown by category with trends',
          path: '/reports/expense-category-report',
          isImplemented: true
        },
        {
          name: 'Expense Journal Entries Report',
          description: 'Detailed Expense journal entries transaction with category, vendor, and payment tracking',
          path: '/reports/journal-entries-party',
          isImplemented: true
        },
        {
          name: 'Item Purchase Summary',
          description: 'Summary of all purchase transactions by supplier and item',
          path: '/reports/item-purchase-summary',
          isImplemented: true
        },
        {
          name: 'Sales Summary - Staff Wise',
          description: 'Sales performance by staff members',
          path: '/reports/sales-summary-staff-wise',
          isImplemented: false
        }
      ]
    },
    {
      title: 'Inventory Reports',
      icon: <InventoryIcon />,
      color: '#f57c00',
      reports: [
        {
          name: 'Item Batch Report',
          description: 'Batch-wise inventory tracking',
          path: '/reports/item-batch-report',
          isImplemented: false
        },
        {
          name: 'Item Report By Party',
          description: 'Items purchased/sold by specific parties',
          path: '/reports/item-report-by-party',
          isImplemented: true
        },
        {
          name: 'Item Sales and Purchase Summary',
          description: 'Combined sales and purchase summary for items',
          path: '/reports/item-sales-and-purchase-summary',
          isImplemented: true
        },
        {
          name: 'Low Stock Summary',
          description: 'Items running low on stock',
          path: '/reports/low-stock-summary',
          isImplemented: true
        },
        {
          name: 'Rate List',
          description: 'Current pricing for all items',
          path: '/reports/rate-list',
          isImplemented: true
        },
        {
          name: 'Custom Item Report',
          description: 'Pick the columns you want — stock, prices, GST, location-wise',
          path: '/reports/custom-item-report',
          isImplemented: true
        },
        {
          name: 'Stock Expiry Report',
          description: 'Batch-wise expiry of every item in stock, location wise',
          path: '/reports/stock-expiry-report',
          isImplemented: true
        },
        {
          name: 'Stock Summary',
          description: 'Summary of current stock levels',
          path: '/reports/stock-summary',
          isImplemented: false
        }
      ]
    },
    {
      title: 'Party Reports',
      icon: <PeopleIcon />,
      color: '#7b1fa2',
      reports: [
        {
          name: 'Party Statement (Ledger)',
          description: 'Complete party transaction ledger',
          path: '/reports/party-ledger',
          isImplemented: true
        },
        {
          name: 'Party Ledger (Journal Entries)',
          description: 'Party ledger from journal entries with filters',
          path: '/reports/party-ledger-journal',
          isImplemented: true
        },
        {
          name: 'Receivable Ageing Report',
          description: 'Outstanding receivables by age',
          path: '/reports/receivable-ageing-report',
          isImplemented: false
        },
        {
          name: 'Party Report By Item',
          description: 'Party-wise item transaction details',
          path: '/reports/party-report-by-item',
          isImplemented: false
        },
        {
          name: 'Party Wise Outstanding',
          description: 'Outstanding amounts by party',
          path: '/reports/party-wise-outstanding',
          isImplemented: true
        },
        {
          name: 'Employee Ledger',
          description: 'Employee-wise salary and wage transaction ledger',
          path: '/reports/employee-ledger',
          isImplemented: true,
          reportType: 'EMPLOYEE_LEDGER'
        },
        // {
        //   name: 'Sales Summary - Category Wise',
        //   description: 'Sales performance by category',
        //   path: '/reports/sales-summary-category-wise',
        //   isImplemented: false
        // }
      ]
    }
  ];

  // Handle navigation based on report type
  const handleNavigateTo = (report: ReportItem) => {
    if (report.reportType) {
      // Special handling for implemented reports
      switch (report.reportType) {
        // case 'TDS_PAYABLE':
        //   navigate('/report/tds-payable');
        //   break;
        // case 'TDS_RECEIVEABLES':
        //   navigate('/report/tds-receivables');
        //   break;
        // case 'TCS_PAYABLE':
        //   navigate('/report/tcs-payable');
        //   break;
        // case 'TCS_RECEIVEABLES':
        //   navigate('/report/tcs-receivables');
        //   break;
        default:
          navigate(report.path);
      }
    } else {
      navigate(report.path);
    }
  };

  // Toggle favorite status
  const toggleFavorite = (reportName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setFavorites(prev => 
      prev.includes(reportName) 
        ? prev.filter(name => name !== reportName)
        : [...prev, reportName]
    );
  };

  // Filter reports based on search term
  const filteredCategories = reportCategories.map(category => ({
    ...category,
    reports: category.reports.filter(report =>
      report.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.reports.length > 0);

  // Get favorite reports, in the order they are listed in `favorites` rather
  // than the order their categories happen to be declared in.
  const allReports = reportCategories.flatMap(category => category.reports);
  const favoriteReports = favorites
    .map(name => allReports.find(report => report.name === name))
    .filter((report): report is ReportItem => !!report);

  return (
    <Box sx={{ 
      backgroundColor: '#f5f5f5', 
      minHeight: '100vh',
      px: { xs: 2, sm: 3, md: 4 }, // Responsive padding
      py: 3 
    }}>
      {/* Header */}
      <Box sx={{ 
        mb: 4, 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'flex-start', 
        gap: 3,
        flexWrap: { xs: 'wrap', md: 'nowrap' } // Stack on mobile
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: '#1a1a1a' }}>
            Reports Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Generate comprehensive reports for GST, transactions, inventory, and party analysis
          </Typography>
        </Box>

        {/* Search Bar */}
        <Box sx={{
          width: { xs: '100%', md: 'auto' },
          minWidth: { xs: 'auto', md: 400 },
          maxWidth: { xs: '100%', md: 500 },
          borderRadius: 4
        }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search Reports ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ backgroundColor: 'white', borderRadius: 4 }}
          />
        </Box>
      </Box>

      {/* Favorites Section */}
      {favoriteReports.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <StarIcon sx={{ color: '#ffc107' }} />
            Favorite Reports
          </Typography>
          <Grid container spacing={2}>
            {favoriteReports.map((report, index) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={index}>
                <Box
                  sx={{
                    cursor: report.isImplemented ? 'pointer' : 'not-allowed',
                    transition: 'all 0.3s ease',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    opacity: report.isImplemented ? 1 : 0.7,
                    border: '1px solid #e0e0e0',
                    borderRadius: 4,          
                    p: 2,
                    backgroundColor: 'white',
                    '&:hover': report.isImplemented ? {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                      borderColor: '#ffc107'
                    } : {}
                  }}
                  onClick={() => report.isImplemented && handleNavigateTo(report)}
                >
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{
                        fontSize: '1rem',
                        lineHeight: 1.2,
                        pr: 1 // Add padding-right to prevent overlap with star
                      }}
                    >
                      {report.name}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => toggleFavorite(report.name, e)}
                      sx={{
                        color: '#ffc107',
                        ml: 1,
                        flexShrink: 0 // Prevent icon from shrinking
                      }}
                    >
                      <StarIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      flex: 1,
                      mb: 1
                    }}
                  >
                    {report.description}
                  </Typography>
                  {!report.isImplemented && (
                    <Chip
                      label="Coming Soon"
                      size="small"
                      sx={{
                        mt: 'auto', // Push to bottom
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        alignSelf: 'flex-start'
                      }}
                    />
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
          <Divider sx={{ mt: 3 }} />
        </Box>
      )}

      {/* Category Filter Pills */}
      <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Chip
          label="All Categories"
          variant={selectedCategory === null ? "filled" : "outlined"}
          onClick={() => setSelectedCategory(null)}
          sx={{ 
            backgroundColor: selectedCategory === null ? '#1976d2' : 'transparent',
            color: selectedCategory === null ? 'white' : '#1976d2',
            '&:hover': { backgroundColor: selectedCategory === null ? '#1565c0' : '#e3f2fd' }
          }}
        />
        {reportCategories.map((category) => (
          <Chip
            key={category.title}
            label={category.title}
            variant={selectedCategory === category.title ? "filled" : "outlined"}
            onClick={() => setSelectedCategory(selectedCategory === category.title ? null : category.title)}
            sx={{ 
              backgroundColor: selectedCategory === category.title ? category.color : 'transparent',
              color: selectedCategory === category.title ? 'white' : category.color,
              borderColor: category.color,
              '&:hover': { 
                backgroundColor: selectedCategory === category.title ? category.color : `${category.color}20` 
              }
            }}
          />
        ))}
      </Box>

      {/* Report Categories Grid */}
      <Grid container spacing={3}>
        {(selectedCategory 
          ? filteredCategories.filter(cat => cat.title === selectedCategory)
          : filteredCategories
        ).map((category, categoryIndex) => (
          <Grid item xs={12} key={categoryIndex}>
            <Box sx={{
              p: 3,
              borderRadius: 4,
              border: '1px solid #e0e0e0',
              backgroundColor: 'white'
            }}>
              {/* Category Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{
                    backgroundColor: category.color,
                    mr: 2,
                    width: 48,
                    height: 48
                  }}
                >
                  {category.icon}
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="bold" sx={{ color: category.color }}>
                    {category.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {category.reports.length} reports available
                  </Typography>
                </Box>
              </Box>

              {/* Reports Grid */}
              <Grid container spacing={2}>
                {category.reports.map((report, reportIndex) => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={reportIndex}>
                    <Box
                      sx={{
                        cursor: report.isImplemented ? 'pointer' : 'not-allowed',
                        height: '100%',
                        transition: 'all 0.3s ease',
                        border: `1px solid #e0e0e0`,
                        borderRadius: 4,
                        opacity: report.isImplemented ? 1 : 0.7,
                        backgroundColor: 'white',
                        p: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        '&:hover': report.isImplemented ? {
                          transform: 'translateY(-4px)',
                          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                          borderColor: category.color
                        } : {}
                      }}
                      onClick={() => report.isImplemented && handleNavigateTo(report)}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography
                          variant="subtitle1"
                          fontWeight="bold"
                          sx={{
                            fontSize: '0.95rem',
                            lineHeight: 1.3,
                            flex: 1
                          }}
                        >
                          {report.name}
                        </Typography>
                        <Tooltip title={favorites.includes(report.name) ? "Remove from favorites" : "Add to favorites"}>
                          <IconButton
                            size="small"
                            onClick={(e) => toggleFavorite(report.name, e)}
                            sx={{ ml: 1 }}
                          >
                            {favorites.includes(report.name) ?
                              <StarIcon fontSize="small" sx={{ color: '#ffc107' }} /> :
                              <StarBorderIcon fontSize="small" />
                            }
                          </IconButton>
                        </Tooltip>
                      </Box>

                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          fontSize: '0.85rem',
                          lineHeight: 1.4,
                          flex: 1
                        }}
                      >
                        {report.description}
                      </Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                        {report.isImplemented ? (
                          <Chip
                            label="Available"
                            size="small"
                            sx={{
                              backgroundColor: `${category.color}20`,
                              color: category.color,
                              fontWeight: 'bold'
                            }}
                          />
                        ) : (
                          <Chip
                            label="Coming Soon"
                            size="small"
                            sx={{
                              backgroundColor: '#f5f5f5',
                              color: '#666'
                            }}
                          />
                        )}

                        <Tooltip title={report.isImplemented ? "Generate Report" : "Coming Soon"}>
                          <span>
                            <IconButton
                              size="small"
                              disabled={!report.isImplemented}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (report.isImplemented) {
                                  handleNavigateTo(report);
                                }
                              }}
                              sx={{
                                color: report.isImplemented ? category.color : '#ccc',
                                '&:hover': report.isImplemented ? { backgroundColor: `${category.color}10` } : {}
                              }}
                            >
                              <FileDownloadIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* No Results Message */}
      {filteredCategories.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <AssessmentIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No reports found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search terms or category filters
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ReportsDashboard;